'use client';

import { VscGithub, VscMail, VscLinkExternal } from 'react-icons/vsc';
import Link from 'next/link';

import styles from '@/styles/AboutPage.module.css';

const AboutPage = () => {
  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerText}>
              <h1 className={styles.name}>Reggie Claassen</h1>
              <p className={styles.role}>Electronic Technician at Royal New Zealand Navy</p>
              <div className={styles.location}>
                <span className={styles.dot} />
                Auckland NZ
              </div>
            </div>
          </div>
          
          <div className={styles.headerActions}>
            <a 
              href="https://github.com/itsnitinr" 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.iconButton}
            >
              <VscGithub size={20} />
            </a>
            <Link href="/contact" className={styles.iconButton}>
              <VscMail size={20} />
            </Link>
          </div>
        </header>

        <div className={styles.content}>
          {/* Bio Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionNumber}>01</span>
              <h2 className={styles.sectionTitle}>About</h2>
            </div>
            
            <div className={styles.sectionBody}>
              <p className={styles.paragraph}>
                Hi, I&apos;m Reghardt (Reggie) Claassen, an Electronic Technician in the Royal New Zealand
                Navy with a passion for technology, networking, Linux, and cyber security.
                Over the past decade, I've worked on maintaining and troubleshooting mission-critical communication and IT systems in demanding operational environments. That experience has taught me the importance of reliability, attention to detail, structured problem-solving, and remaining calm under pressure.
              </p>
              
              <p className={styles.paragraph}>
                In 2025, I completed a Bachelor of Science in Computer Science through Massey University, strengthening my knowledge of software development, networking, operating systems, databases, and cybersecurity. Since graduating, I've been focused on expanding my practical skills by building projects and operating my own homelab.
              </p>

              <p className={styles.paragraph}>
                My homelab is where I enjoy experimenting with technologies such as Docker, Linux, reverse proxies, Cloudflare, Wazuh, Jellyfin, Immich, Nginx Proxy Manager, automation, and self-hosted services. These projects allow me to gain hands-on experience with modern infrastructure while continuously improving my understanding of system administration and security.  
              </p>

              <p className={styles.paragraph}>
                I'm currently working towards a transition into the cyber security field, with a particular interest in defensive security, security operations, infrastructure, and network security. I'm also studying towards industry certifications including CCNA, CompTIA Network+, Security+, and PenTest+ to further develop my technical knowledge.
              </p>

              <p className={styles.paragraph}>
                When I'm not building projects or learning new technologies, I enjoy solving technical problems, developing software, and finding ways to automate repetitive tasks. I believe the best way to learn is by building real-world solutions, and this portfolio showcases that ongoing journey.
              </p>

              <p className={styles.paragraph}>
                Thank you for taking the time to visit my portfolio. Feel free to explore my projects, GitHub repositories, and homelab to see what I've been working on.
              </p>
            </div>
          </section>

          {/* Experience Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionNumber}>02</span>
              <h2 className={styles.sectionTitle}>Experience</h2>
            </div>
            
            <div className={styles.sectionBody}>
              <div className={styles.experienceCard}>
                <div className={styles.expMeta}>
                  <span className={styles.expPeriod}>Education</span>
                </div>
                <h3 className={styles.expRole}>Bacholar of Science (Computer Science)</h3>
                <p className={styles.expCompany}>Massey University</p>
                <ul className={styles.expList}>
                  <li>Micro-Controller</li>
                  <li>Object-Orientated Programming</li>
                  <li>Algorithms</li>
                  <li>Artificial intelligence Programming</li>
                  <li>Games Programming</li>
                </ul>
              <div style={{ margin: "2rem 0" }}></div>
                <h3 className={styles.expRole}>Electrical Engineering Level 4</h3>
                <p className={styles.expCompany}>Royal New Zealand Navy</p>
                <ul className={styles.expList}>
                  <li>Analogue Electronics</li>
                  <li>Digital Electronics</li>
                  <li>Soldiering</li>
                  <li>Microcontrollers</li>
                  <li>Radio/RADAR Equipment</li>
                  <li>PC Maintenance</li>
                </ul>

              </div>

              <div className={styles.experienceCard}>
                <div className={styles.expMeta}>
                  <span className={styles.expPeriod}>Present</span>
                </div>
                <h3 className={styles.expRole}>Electronic Technician</h3>
                <p className={styles.expDesc}>
                <ul className={styles.expList}>
                  <li>Micro-Controller</li>
                  <li>Circuit Boards</li>
                  <li>Soldiering</li>
                  <li>Communication Systems</li>
                  <li>Radio Equipment Maintenance</li>
                  <li>Local Area Network Maintenance</li>
                  <li>Server Maintenance</li>
                  <li>Server Administration (e.g. Active Directory)</li>
                </ul>
                </p>
              </div>
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionNumber}>03</span>
              <h2 className={styles.sectionTitle}>Skills</h2>
            </div>
            
            <div className={styles.sectionBody}>
              <div className={styles.skillsGrid}>
                <div className={styles.skillCategory}>
                  <h4 className={styles.skillTitle}>Programming Languages</h4>
                  <div className={styles.skillTags}>
                    <span className={styles.skillTag}>C, C++</span>
                    <span className={styles.skillTag}>Python</span>
                    <span className={styles.skillTag}>Java</span>
                    <span className={styles.skillTag}>C#</span>
                  </div>
                </div>
                

                <div className={styles.skillCategory}>
                  <h4 className={styles.skillTitle}>SYSTEMS & NETWORKING</h4>
                  <div className={styles.skillTags}>
                    <span className={styles.skillTag}>Windows Server</span>
                    <span className={styles.skillTag}>Linux</span>
                    <span className={styles.skillTag}>Active Directory</span>
                    <span className={styles.skillTag}>TCP/IP</span>
                    <span className={styles.skillTag}>DNS</span>
                    <span className={styles.skillTag}>DHCP</span>
                    <span className={styles.skillTag}>Cisco Networking</span>
                  </div>
                </div>
                

                <div className={styles.skillCategory}>
                  <h4 className={styles.skillTitle}>CYBER & HOMELAB</h4>
                  <div className={styles.skillTags}>
                    <span className={styles.skillTag}>Network Security</span>
                    <span className={styles.skillTag}>Wazuh / SIEM</span>
                    <span className={styles.skillTag}>Reverse-Proxy</span>
                    <span className={styles.skillTag}>Cloudflare</span>
                    <span className={styles.skillTag}>Virtualisation</span>

                  </div>
                </div>

                <div className={styles.skillCategory}>
                  <h4 className={styles.skillTitle}>DEVELOPMENT & TOOLS</h4>
                  <div className={styles.skillTags}>
                    <span className={styles.skillTag}>Git</span>
                    <span className={styles.skillTag}>VS Code</span>
                    <span className={styles.skillTag}>Docker</span>
                    <span className={styles.skillTag}>Linux</span>
                    <span className={styles.skillTag}>Wireshark</span>
                    <span className={styles.skillTag}>Nmap</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Writing Section */}
          {/*<section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionNumber}>04</span>
              <h2 className={styles.sectionTitle}>Writing</h2>
            </div>
            
            <div className={styles.sectionBody}>
              <p className={styles.paragraph}>
                I&apos;ve had the pleasure of writing for some amazing publications 
                as a freelance technical author:
              </p>
              
              <div className={styles.writingLinks}>
                <a 
                  href="https://www.100ms.live/blog/author/nitin" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.writingLink}
                >
                  <span>100ms Blog</span>
                  <VscLinkExternal size={14} />
                </a>
                
                <a 
                  href="https://blog.logrocket.com/author/nitinranganath/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.writingLink}
                >
                  <span>LogRocket Blog</span>
                  <VscLinkExternal size={14} />
                </a>
                
                <a 
                  href="https://dev.to/itsnitinr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={styles.writingLink}
                >
                  <span>DEV.to</span>
                  <VscLinkExternal size={14} />
                </a>
              </div>
            </div>
          </section>*/}

          {/* Beyond Code Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionNumber}>04</span>
              <h2 className={styles.sectionTitle}>Beyond Code</h2>
            </div>
            
            <div className={styles.sectionBody}>
              <p className={styles.paragraph}>
                Aside from programming, I enjoy spending time with my family. 
                
              </p>
            </div>
          </section>
        </div>

        <footer className={styles.footer}>
          <Link href="/projects" className={styles.footerLink}>
            View my projects →
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default AboutPage;
